/**
 * English strings the real client adds (the prototype's own strings are in
 * proto.generated.ts). `auth.*`, `notice.*`, `security.*`, `devices.*` and
 * `recovery.*` are user-management §2.11 word for word; `ns.*` are ours.
 */
import type { Dict } from './core';

export const en: Dict = {
  // user-management §2.11
  'auth.signup.title': 'Create your account',
  'auth.username.label': 'Username',
  'auth.username.help': '2-32 characters: English letters, numbers, _ and .',
  'auth.username.latinHint':
    'Usernames use English letters, numbers, _ and . only. Your display name can be in any language.',
  'auth.username.reserved': 'That username is reserved. Please choose another.',
  'auth.username.doubleDot': "A username can't have two dots in a row.",
  'auth.displayName.label': 'Display name (optional)',
  'auth.displayName.help': 'This is how people see you. Any language, up to 32 characters.',
  'auth.displayName.protected':
    'This name is too close to a staff or official name. Please choose another.',
  'auth.password.label': 'Password',
  'auth.password.help': 'At least 8 characters. A long phrase works best.',
  'auth.password.breached': 'This password has appeared in a data leak. Please choose another.',
  'auth.birth.label': 'Date of birth',
  'auth.birth.help':
    '{brand} is for adults aged 18 and over. We use your date of birth only to check that. We never ask for ID.',
  'auth.birth.tooYoung':
    "You need to be at least 18 to use {brand}. We didn't create an account or keep your date of birth.",
  'auth.pow.working': "Checking you're not a bot…",
  'auth.alpha.code': 'Alpha invite code',
  'auth.alpha.invalid': "That invite code isn't valid or has been used.",
  'auth.signup.submit': 'Create account',
  'auth.codes.title': 'Save your recovery codes',
  'auth.codes.body':
    "If you lose your password or your phone, these codes are the only sure way back in. Each one works once. We can't recover your account for you.",
  'auth.codes.copy': 'Copy',
  'auth.codes.confirm': 'I saved my recovery codes',
  'auth.codes.where':
    "Write them on paper or keep them in a password manager. Don't save them in a file on this device: malware looks for such files.",
  'auth.signin.title': 'Welcome back',
  'auth.signin.wrong': 'The email or password is wrong.',
  'auth.signin.wait': 'Too many tries. Wait {n} seconds, then try again.',
  'auth.mfa.title': 'Two-step verification',
  'auth.mfa.totp': 'Enter the 6-digit code from your authenticator app.',
  'auth.mfa.wrong':
    "That code didn't work. Check that your phone's clock is right, then try again.",
  'auth.mfa.useCode': 'Use a recovery code',
  'recovery.code': 'Use a recovery code',
  // Discord's "My Account": email, two-step, password, account removal (2026-10-29)
  'security.title': 'Account',
  'security.2fa.setup':
    'Scan this with an authenticator app, then enter the code it shows. It works without internet.',
  'security.2fa.on': 'Two-step verification is on.',
  'security.2fa.off.warn': 'Turning this off makes your account easier to take over.',
  'security.codes.regen': 'Get new recovery codes (the old ones stop working)',
  'security.stepUp.title': "Confirm it's you",
  'security.stepUp.body': 'Enter your password or a two-step code to continue.',
  'security.password.signOutOthers': 'Sign out of all other devices',
  'devices.title': 'Devices',
  'devices.this': 'This device',
  'devices.city': 'Near {city} (approximate)',
  'devices.signOut': 'Sign out',
  'devices.signOutOthers': 'Sign out of all other devices',

  // ours
  'ns.brand': 'Neosocial',
  'ns.loading': 'Loading…',
  'ns.loadingOlder': 'Loading older messages…',
  'ns.saved': 'Saved',
  'ns.signout': 'Sign out',
  'ns.conn.connecting': 'Connecting to Neosocial…',
  'ns.conn.reconnecting': 'Reconnecting…',
  'ns.offline.title': "Can't reach the server",
  'ns.offline.body':
    "You're still signed in. We'll keep your session; try again when the connection is back.",
  'ns.auth.foot': 'Closed alpha. Adults only (18+). No ID checks, ever.',
  'ns.signin.submit': 'Sign in',
  'ns.signin.new': 'New here?',
  'ns.signup.have': 'Already have an account?',
  'ns.username.taken': 'That username is taken. Try another.',
  'ns.underage.title': 'Not yet',

  // Inter Tight is gone (T4); one family per script. Defined here (not only
  // overridden) so the prototype's old wording is not ported at all.
  'set.about.fonts':
    'Fonts: Inter, and a content font for Arabic script, both under the SIL Open Font License.',
  'ns.birth.day': 'Day',
  'ns.birth.month': 'Month',
  'ns.birth.year': 'Year',
  'ns.birth.invalid': "That date doesn't exist. Please check it.",
  'ns.dn.empty': 'Please enter a name.',
  'ns.dn.too_long': 'Up to 32 characters.',
  'ns.dn.blank': 'A name needs at least one visible letter, number or emoji.',
  'ns.mfa.code': 'Code',
  'ns.mfa.verify': 'Verify',
  'ns.mfa.useApp': 'Use the authenticator app',
  'ns.mfa.recoveryHelp': 'Enter one of your recovery codes. Each one works once.',
  'ns.mfa.expired': 'This sign-in took too long. Go back and sign in again.',
  'ns.mfa.off': 'Two-step verification is off.',
  'ns.mfa.offSub': 'Add a code from an authenticator app at sign-in. It works without internet.',
  'ns.mfa.turnOn': 'Turn on',
  'ns.mfa.enable': 'Turn on two-step',
  'ns.mfa.disable': 'Turn off',
  'ns.mfa.disabled': 'Two-step verification is off.',
  'ns.mfa.manual': "Can't scan? Enter this key:",
  'ns.mfa.codeOrRecovery': 'Authenticator code or recovery code',
  'ns.mfa.newCodes': 'New codes',
  'ns.pw.title': 'Password',
  'ns.pw.current': 'Current password',
  'ns.pw.new': 'New password',
  'ns.pw.change': 'Change password',
  'ns.pw.changed': 'Password changed.',
  'ns.pw.wrong': "That isn't your current password.",
  'ns.sessions.lastUsed': 'last used {when}',
  'ns.sessions.failed': "Couldn't load your devices.",
  'ns.sessions.othersBody':
    'Every other device will be signed out right away. This one stays signed in.',
  'ns.sessions.othersDone': 'Signed out of every other device.',
  'ns.client.web': 'Web',
  'ns.client.desktop': 'Desktop app',
  'ns.client.android': 'Android',
  'ns.client.ios': 'iOS',
  'ns.client.unknown': 'Unknown app',
  'ns.set.profile': 'Profile',
  'ns.set.voice': 'Voice & audio',
  'ns.set.notifications': 'Notifications',
  'ns.set.desktopNotif': 'Enable desktop notifications',
  'ns.set.desktopNotifSub':
    "Windows' own notifications for DMs, mentions, replies, calls, pokes and friend requests.",
  'ns.set.webNotifSub':
    'Shown by your browser for DMs, mentions, replies, calls, pokes and friend requests while this tab is open.',
  'ns.set.notifBlocked':
    'Your browser blocks notifications for this site. Allow them in its site settings.',
  'ns.about.server': 'Server: {origin}',
  'ns.about.noForeign': 'Fonts and images are bundled. The app talks only to its own server.',
  'ns.upd.ready': 'Neosocial {version} is ready to install.',
  'ns.upd.restart': 'Restart to update',
  'ns.upd.installing': 'Installing the update…',
  'ns.upd.failed': "The update couldn't be installed. Try again later.",
  'ns.upd.voiceNote': 'Restarting ends your voice call.',
  'ns.upd.checking': 'Checking for updates…',
  'ns.upd.downloading': 'Downloading Neosocial {version}…',
  'ns.upd.upToDate': 'You have the latest version.',
  'ns.upd.error': "Couldn't check for updates.",
  'ns.upd.downloadFailed': "The update couldn't be downloaded. Try again later.",
  'ns.upd.toast': 'Update ready: Neosocial {version}.',
  'ns.upd.toastRestart': 'Restart',
  // the web app's Windows download banner (shell/DesktopDownloadBar.tsx)
  'dl.bar.label': 'Neosocial for Windows',
  'dl.bar.title': 'Get the Neosocial app for Windows:',
  'dl.bar.body': 'global push-to-talk and hotkeys, better voice.',
  'dl.bar.download': 'Download',
  'dl.bar.hide': 'Hide for 30 days',
  'ns.deletedAccount': 'Deleted account',
  'ns.prof.avatar': 'Change avatar',
  'ns.prof.removeAvatar': 'Remove',
  'ns.prof.status': 'Custom status',
  'ns.prof.pronouns': 'Pronouns',
  'ns.prof.bioLeft': '{n} characters left',
  'ns.err.generic': 'Something went wrong. Please try again.',
  'ns.err.network': "Can't reach the server. Check your connection and try again.",
  'ns.err.429': 'Slow down a little, then try again.',
  'ns.err.guildLimit.day': {
    one: 'You can create up to {n} server a day.',
    other: 'You can create up to {n} servers a day.',
  },
  'ns.err.guildLimit.hour': {
    one: 'You can create up to {n} server an hour.',
    other: 'You can create up to {n} servers an hour.',
  },
  'ns.err.tryInHours': {
    one: 'Try again in about an hour.',
    other: 'Try again in about {n} hours.',
  },
  'ns.err.tryInMinutes': {
    one: 'Try again in about a minute.',
    other: 'Try again in about {n} minutes.',
  },
  'ns.err.tryLater': 'Try again later.',
  'ns.err.10003': 'That channel no longer exists.',
  'ns.err.10008': 'That message no longer exists.',
  'ns.err.10013': "We couldn't find that person.",
  'ns.err.30002': "You've reached the friend limit.",
  'ns.err.30003': 'This channel has reached its pin limit.',
  'ns.err.30004': 'A group can have at most 10 people.',
  'ns.err.30010': 'This message has reached its reaction limit.',
  'ns.err.40005': 'That file is too large.',
  'ns.err.50001': "You don't have access to that.",
  'ns.err.50006': "You can't send an empty message.",
  'ns.err.50007': "You can't message this person.",
  'ns.err.50013': "You don't have permission to do that.",
  'ns.err.50035': 'Some details need fixing.',
  'ns.err.80001': "This person isn't accepting friend requests.",
  'ns.err.100001': "Confirm it's you first.",
  'ns.err.100011': "That didn't match. Check it and try again.",
  'ns.err.100012': "That invite code isn't valid or has been used.",
  'ns.err.100020': 'That channel needs a password.',
  'ns.err.100022': 'That channel is full.',
  'ns.err.100023': 'Banned from that channel.',
  'ns.err.100024': "You're timed out for now.",
  'ns.err.100025': "That role is above yours, so you can't change it.",
  'ns.jump.title': 'Search or jump to',
  'ns.jump.placeholder': 'Channels, people… (# @ > narrow it)',
  'ns.hub.noChannels': 'There are no channels you can see yet.',
  'ns.hub.noChannelsTitle': 'Nothing here yet',
  'ns.ch.markRead': 'Mark as read',
  'ns.typing.one': '{a} is typing…',
  'ns.typing.two': '{a} and {b} are typing…',
  'ns.typing.many': '{n} people are typing…',
  'ns.msg.edit': 'Edit',
  'ns.msg.editHint': 'Enter to save · Esc to cancel',
  'ns.msg.delete': 'Delete message',
  'ns.msg.deleteTitle': 'Delete this message?',
  'ns.msg.deleteBody': "It's removed for everyone. This can't be undone.",
  'ns.msg.copy': 'Copy text',
  'ns.msg.pin': 'Pin message',
  'ns.msg.unpin': 'Unpin message',
  'ns.msg.pinned': 'Pinned',
  'ns.msg.unpinned': 'Unpinned',
  'ns.msg.refGone': 'Original message was deleted',
  'ns.msg.failed': "Couldn't send.",
  'ns.msg.retry': 'Try again',
  'ns.msg.discard': 'Discard',
  'ns.msg.files': { one: '{n} image', other: '{n} images' },
  'ns.msg.historyFailed': "Couldn't load messages.",
  'ns.sys.join': 'joined the community.',
  'ns.sys.added': 'was added to the group.',
  'ns.sys.pinned': 'pinned a message.',
  'ns.pins.title': 'Pinned messages',
  'ns.pins.none': 'No pinned messages yet.',
  'ns.pins.error': "Couldn't load pinned messages.",
  'ns.cmp.attach': 'Upload an image',
  'ns.cmp.imagesOnly': 'Only images can be uploaded in the alpha.',
  'ns.cmp.tooLong': 'Messages can be up to {n} characters.',
  'ns.cmp.noSend': "You can't send messages in {name}.",
  'ns.cmp.replying': 'Replying to',
  'ns.cmp.phDm': 'Message {name}',
  // a voice or stage channel's own chat: its name without '#' (Discord)
  'ns.cmp.phVoice': 'Message {name}',
  'ns.start.voiceTitle': 'Welcome to {name}!',
  'ns.start.voiceSub': 'This is the start of the {name} channel.',
  'ns.a11y.messagesIn': 'Messages in {name}',
  'ns.cmp.remove': 'Remove {name}',
  'ns.dm.none': 'No conversations yet.',
  'ns.dm.start': 'This is the start of your conversation with {name}.',
  'ns.dm.pick': 'Pick up to {n} friends.',
  'ns.dm.createGroup': 'Create group',
  'ns.dm.noFriends': 'No friends to add yet.',
  'ns.fr.addSub': 'Add a friend by their username.',
  'ns.fr.addPh': 'Username, for example sara.k',
  'ns.fr.sent': 'Friend request sent to {name}.',
  'ns.fr.outgoing': 'Outgoing friend request',
  'ns.fr.cancel': 'Cancel request',
  'ns.fr.block': 'Block',
  'ns.fr.unblock': 'Unblock',
  'ns.fr.blockedSub': 'Blocked',
  'ns.fr.empty': 'No one here yet.',
  'ns.voice.lost': 'Voice disconnected',
  'ns.voice.lostIn': 'Disconnected from {name}',
  'ns.voice.rejoin': 'Join again',
  'ns.set.sounds': 'Sounds',
  'ns.snd.title': 'Sounds',
  'ns.snd.enabled': 'Play sounds',
  'ns.snd.enabledSub':
    'Short, soft cues for voice and alerts. Made by Neosocial, played on this device.',
  'ns.snd.volume': 'Sound volume',
  'ns.snd.which': 'Play a sound when',
  'ns.snd.preview': 'Play the sound for: {name}',
  'ns.snd.deafNote': 'While you are deafened, only the mute and deafen sounds play.',
  'ns.snd.k.join': 'You join a voice channel',
  'ns.snd.k.leave': 'You leave a voice channel',
  'ns.snd.k.move': 'You move to another channel',
  'ns.snd.k.peerJoin': 'Someone joins your channel',
  'ns.snd.k.peerLeave': 'Someone leaves your channel',
  'ns.snd.k.mute': 'You mute or unmute',
  'ns.snd.k.deafen': 'You deafen or undeafen',
  'ns.snd.k.ptt': 'You press or release push-to-talk (very soft)',
  'ns.snd.k.connection': 'Voice disconnects or reconnects',
  'ns.snd.k.poke': 'You get a poke',
  'ns.snd.k.message': 'A mention or DM arrives while the window is in the background',
  'ns.voice.couldNot': "Couldn't join {channel}: {why}",
  'ns.voice.couldNotBecause': "Couldn't join {channel}. {why}",
  'ns.voice.pwBody': 'This room has a password.',
  'ns.voice.err.password_required': 'it needs a password',
  'ns.voice.err.bad_password': 'wrong password',
  'ns.voice.err.not_allowed': "you're not allowed in",
  'ns.voice.err.full': "it's full",
  'ns.voice.err.banned': "you're banned from it",
  'ns.voice.err.no_server': "the voice server didn't answer",
  'ns.voice.err.auth': 'your session needs a refresh',
  'ns.voice.err.media': "audio couldn't connect",
  'ns.voice.err.lost': 'the connection dropped',
  'ns.voice.err.kicked': 'a moderator disconnected you',
  'ns.voice.err.replaced': 'you joined from another device',
  'ns.voice.err.left_elsewhere': 'you disconnected from another device',
  // a role or overwrite change took the channel away: no moderator did it
  'ns.voice.err.lost_access': 'you no longer have access to {channel}',
  'ns.voice.movedLostAccess': 'You no longer have access to {from}, so you were moved to {to}',
  'ns.voice.err.unknown': 'something went wrong',
  'ns.voice.volume': 'Volume',
  'ns.voice.muteLocal': 'Mute for me',
  'ns.voice.unmuteLocal': 'Unmute for me',
  'ns.voice.localNote': 'Only you are affected. The server stops sending you their voice.',
  'ns.voice.listenOnly': 'No microphone: listening only',
  'ns.voice.noMicTip': 'No microphone: listening only. Choose one in Voice & audio.',
  'ns.voice.micFallback':
    "Your saved microphone isn't connected, so the system default microphone is on.",
  'ns.voice.micMissing': 'Saved microphone (not connected)',
  'ns.voice.micMissingNote':
    "Your saved microphone isn't connected. Voice uses the system default until it's back.",
  'ns.voice.reconnecting': 'Reconnecting…',
  'ns.voice.reconnectingTo': 'Reconnecting to {name}…',
  'ns.voice.hold': 'Hold',
  'ns.voice.toTalk': 'to talk',
  'ns.voice.pttFocused': 'Works while this window is focused',
  'ns.voice.pttBlurred': 'Window not focused: push-to-talk is paused',
  'ns.voice.devices': 'Devices',
  'ns.voice.input': 'Microphone',
  'ns.voice.output': 'Speakers or headphones',
  'ns.voice.outputNo': "This browser can't choose an output; it uses the system's.",
  'ns.voice.default': 'System default',
  'ns.voice.micN': 'Microphone {n}',
  'ns.voice.spkN': 'Output {n}',
  'ns.voice.showNames': 'Allow the microphone to see device names',
  'ns.voice.inputMode': 'Input mode',
  'ns.voice.pttKey': 'Push-to-talk key',
  'ns.voice.pttWebNote':
    'In the browser it works only while this window is focused. The desktop app works everywhere.',
  'ns.voice.pttDesktopNote': 'Works even while a game has focus.',
  'ns.voice.release': 'Release delay',
  'ns.voice.releaseSub': 'Keeps sending a moment after you let go, so word endings are not cut.',
  'ns.voice.sensitivity': 'Input sensitivity',
  'ns.voice.sensitivitySub': 'Talk to test: the bar turns green when you would be heard.',
  'ns.voice.meter': 'Microphone level',
  'ns.voice.test': 'Test microphone',
  'ns.voice.stopTest': 'Stop test',
  'ns.voice.noMic': "The microphone couldn't be opened. Check the browser's permission.",
  'ns.voice.processing':
    "Echo cancellation, noise suppression and gain control use your browser's built-in processing.",
  'ns.key.press': 'Press a key…',
  'ns.key.pressCombo': 'Press keys…',
  'ns.key.none': 'Not set',
  'ns.key.record': 'Record keybind',
  'ns.key.clear': 'Clear',
  'ns.key.keybinds': 'Keybinds',
  'ns.key.toggleMute': 'Toggle mute',
  'ns.key.toggleDeafen': 'Toggle deafen',
  'ns.key.webNote': 'Works while Neosocial is the focused tab, even while you type.',
  'ns.key.desktopNote': 'Works everywhere, even while a game has focus.',
  'ns.key.reserved':
    'Your browser or system keeps this combination for itself, so it may not reach Neosocial.',
  'ns.key.conflict': 'Another keybind uses this combination.',
  'ns.voice.pwTitle': '{channel} needs a password',
  'ns.voice.pwLabel': 'Channel password',
  'ns.voice.pwWrong': "That password isn't right. Try again.",
  'ns.voice.va': 'Voice activation',
  'ns.voice.ptt': 'Push to talk',
  'ns.poke.title': 'Poke {name}',
  'ns.poke.label': 'Message',
  'ns.poke.ph': 'Short and sweet',
  'ns.poke.q1': 'On my way',
  'ns.poke.q2': 'Give me 5 minutes',
  'ns.poke.q3': "Can't talk right now",
  'ns.poke.left': '{n} left',
  'ns.poke.send': 'Send poke',
  'ns.poke.sent': 'Poke sent to {who}',
  'ns.poke.got': '{who} poked you',
  'ns.poke.reply': 'Reply',
  'ns.addServer.label': 'Add a server',
  'ns.addServer.body':
    "Creating your own server isn't available yet. Everyone is in the Neosocial community for now; it's coming in a future update.",
  'ns.addServer.ok': 'OK',
  'ns.rail.unread': { one: '{n} unread', other: '{n} unread' },
  'ns.rail.requests': { one: '{n} friend request', other: '{n} friend requests' },
  'ns.listSep': ', ',
  'ns.copy.failed': 'This browser didn’t allow copying. Select it and copy it yourself: {text}',
  'ns.invite.invalid': 'This invite link is invalid or has expired.',
  'ns.invite.offline': 'Couldn’t check the invite link. Check your connection and open it again.',
  'ns.invite.to': 'You’re invited to {server}',
  'ns.invite.toChannel': 'You’re invited to {server} · {channel}',
  'ns.invite.signIn': 'Create an account or sign in, and it opens right after.',
  'ns.invite.checking': 'Checking the invite link…',
  'ns.invite.joinTitle': 'Join {server}?',
  'ns.invite.joinBody': 'This invite opens {channel}.',
  'ns.invite.join': 'Join',
  'ns.invite.cantSee': 'You can’t open #{channel}: your roles don’t let you see it.',
  'ns.invite.banned': 'You’re banned from {server}, so this invite can’t let you in.',
  'ns.admin.title': 'Server settings',
  'ns.admin.overview': 'Overview',
  'ns.ov.icon': 'Server icon',
  'ns.ov.iconHint':
    'Any image works: it is cropped to a square and made smaller for you. We recommend at least 512 × 512.',
  'ns.ov.upload': 'Upload image',
  'ns.ov.remove': 'Remove',
  'ns.ov.name': 'Server name',
  'ns.ov.nameLength': 'A server name is 2 to 100 characters.',
  'ns.admin.manageChannels': 'Manage channels',
  'ns.admin.manageRoles': 'Manage roles',
  'ns.admin.noTools': "Your roles don't include server tools.",
  'ns.admin.newChannel': 'Create channel',
  'ns.admin.newChannelIn': 'Create a channel in {name}',
  'ns.admin.newCategory': 'Create category',
  'ns.admin.editChannel': 'Edit channel',
  'ns.admin.pickChannel': 'Pick a channel to edit, or create one.',
  'ns.admin.type': 'Type',
  'ns.admin.text': 'Text',
  'ns.admin.voice': 'Voice',
  'ns.admin.category': 'Category',
  'ns.admin.noCategory': 'No category',
  'ns.admin.name': 'Name',
  'ns.admin.topic': 'Topic',
  'ns.admin.userLimit': 'User limit',
  'ns.admin.userLimitHelp': '0 means no limit (up to 99).',
  'ns.admin.bitrate': 'Bitrate (kbps)',
  'ns.admin.password': 'Channel password',
  'ns.admin.passwordNone': 'No password',
  'ns.admin.passwordKeep': 'Set — type to change',
  'ns.admin.passwordClear': 'Remove the password',
  'ns.admin.overwrites': 'Permissions in this channel',
  'ns.admin.owNoPerm': 'You need Manage Roles to change channel permissions.',
  'ns.admin.owAdd': 'Add',
  'ns.admin.owPick': 'Add a role…',
  'ns.admin.owRemove': 'Remove these permissions',
  'ns.admin.create': 'Create',
  'ns.admin.delete': 'Delete',
  'ns.admin.deleteChannel': 'Delete {name}?',
  'ns.admin.deleteChannelBody':
    'It goes to Recently deleted with its messages and settings. The owner and Admins can restore it for 7 days.',
  'ns.admin.newRole': 'Create role',
  'ns.admin.editRole': 'Edit role',
  'ns.admin.pickRole': 'Pick a role to edit, or create one.',
  'ns.admin.color': 'Colour',
  'ns.admin.noColor': 'No colour',
  'ns.admin.hoist': 'Show separately in the member list',
  'ns.admin.mentionable': 'Anyone can @mention this role',
  'ns.admin.everyoneNote': 'These are the permissions every member has.',
  'ns.admin.roleAbove': "This role is at or above your highest role, so you can't change it.",
  'ns.admin.notYours': "you don't have it",
  'ns.admin.deleteRole': 'Delete the role {name}?',
  'ns.admin.deleteRoleBody':
    'Members lose this role now. The owner and Admins can restore it, with its members, for 7 days.',
  'ns.ow.allow': 'Allow',
  'ns.ow.deny': 'Deny',
  'ns.ow.inherit': 'Inherit',
  'ns.perm.g.general': 'General',
  'ns.perm.g.text': 'Text',
  'ns.perm.g.voice': 'Voice',
  'ns.perm.VIEW_CHANNEL': 'View channels',
  'ns.perm.MANAGE_CHANNELS': 'Manage channels',
  'ns.perm.MANAGE_ROLES': 'Manage roles',
  'ns.perm.MANAGE_GUILD': 'Manage server',
  'ns.perm.VIEW_AUDIT_LOG': 'View audit log',
  'ns.perm.CHANGE_NICKNAME': 'Change nickname',
  'ns.perm.MANAGE_NICKNAMES': 'Manage nicknames',
  'ns.perm.SEND_MESSAGES': 'Send messages',
  'ns.perm.EMBED_LINKS': 'Embed links',
  'ns.perm.ATTACH_FILES': 'Attach images',
  'ns.perm.ADD_REACTIONS': 'Add reactions',
  'ns.perm.MENTION_EVERYONE': 'Mention @everyone and @here',
  'ns.perm.MANAGE_MESSAGES': 'Manage messages',
  'ns.perm.PIN_MESSAGES': 'Pin messages',
  'ns.perm.READ_MESSAGE_HISTORY': 'Read message history',
  'ns.perm.CONNECT': 'Connect',
  'ns.perm.SPEAK': 'Speak',
  'ns.perm.USE_VAD': 'Use voice activity',
  'ns.perm.PRIORITY_SPEAKER': 'Priority speaker',
  'ns.perm.MUTE_MEMBERS': 'Mute members',
  'ns.perm.DEAFEN_MEMBERS': 'Deafen members',
  'ns.perm.MOVE_MEMBERS': 'Move members',
  'ns.perm.POKE': 'Poke',
  'ns.perm.BYPASS_CHANNEL_PASSWORD': 'Skip channel passwords',
  'ns.perm.KICK_MEMBERS': 'Kick members',
  'ns.perm.BAN_MEMBERS': 'Ban members',
  'ns.perm.MODERATE_MEMBERS': 'Time out members',
  'ns.perm.ADMINISTRATOR': 'Administrator',
};
